import axios from 'axios';
import { PEOPLE_API_PATH } from 'constants/api';
import { PeopleInterface } from 'type/peopleType';
import { CastData, CastApiRequest } from '../../types/i18n/i18nTypes';
import { FetchResult } from '../../type/apiType';
import { ApiService } from './apiService';

class PeopleApiService extends ApiService<PeopleInterface> {
  /**
   * IDs로 조회 시 백엔드가 페이지네이션 없이 배열을 직접 반환하는 경우를 정규화
   */
  async fetchByIds(
    ids: number[],
    queryParams?: Record<string, string>
  ): Promise<FetchResult<PeopleInterface>> {
    const result = await super.fetchByIds(ids, queryParams);
    const data = result as unknown as PeopleInterface[] | FetchResult<PeopleInterface>;
    if (Array.isArray(data)) {
      return {
        count: data.length,
        next: null,
        previous: null,
        page_next: null,
        page_previous: null,
        page_size: data.length,
        results: data,
      };
    }
    return data;
  }
  /**
   * i18n 필드를 포함한 Cast 목록 조회
   */
  async fetchCastWithI18n(params?: URLSearchParams): Promise<FetchResult<CastData>> {
    const searchParams = new URLSearchParams(params);

    // i18n 필드를 포함한 필드 목록 설정
    const fields = [
      'id',
      'name',
      'name_en',
      'i18n_name',
      'birth_date',
      'nationality',
      'thumbnail',
      'type',
      'slug',
      'status',
      'created',
      'modified',
    ].join(',');

    searchParams.set('fields', fields);

    const response = await axios.get<FetchResult<CastData>>(this.constructUrl(), {
      params: searchParams,
    });

    return response.data;
  }

  /**
   * i18n 필드를 포함한 단일 Cast 조회
   */
  async fetchCastByIdWithI18n(id: number): Promise<CastData> {
    const fields = [
      'id',
      'name',
      'name_en',
      'i18n_name',
      'birth_date',
      'nationality',
      'thumbnail',
      'type',
      'slug',
      'status',
      'created',
      'modified',
    ].join(',');

    const response = await axios.get<CastData>(this.constructUrl(id), {
      params: { fields },
    });

    return response.data;
  }

  /**
   * i18n 필드를 포함한 Cast 생성
   */
  async createCastWithI18n(castData: CastApiRequest): Promise<CastData> {
    const response = await axios.post<CastData>(this.constructUrl(), castData);
    return response.data;
  }

  /**
   * i18n 필드를 포함한 Cast 업데이트
   */
  async updateCastWithI18n(id: number, castData: Partial<CastApiRequest>): Promise<CastData> {
    const response = await axios.patch<CastData>(this.constructUrl(id), castData);
    return response.data;
  }

  /**
   * 특정 언어의 Cast 목록 조회
   */
  async fetchCastByLanguage(
    language: string,
    params?: URLSearchParams
  ): Promise<FetchResult<CastData>> {
    const searchParams = new URLSearchParams(params);
    searchParams.set('language', language);

    return this.fetchCastWithI18n(searchParams);
  }

  /**
   * 번역 완성도별 Cast 조회
   */
  async fetchCastByTranslationStatus(
    requiredLanguages: string[] = ['ko'],
    params?: URLSearchParams
  ): Promise<FetchResult<CastData>> {
    const searchParams = new URLSearchParams(params);
    searchParams.set('required_languages', requiredLanguages.join(','));

    return this.fetchCastWithI18n(searchParams);
  }
}

export const peopleApiService = new PeopleApiService(PEOPLE_API_PATH);
